import { type ReactNode } from 'react';

// Utils
import { useUserGroupRestriction } from '@/utils/hooks/acl/useUserGroupRestriction';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

// Lib
import { GROUPS, SOCIAL_PROFILES } from '@/lib/routes/privateRoutes';

// Components
import H2 from '@/components/base/Headings/H2';
import { ModalUpsell } from '@/components/composite/Modals/ModalUpsell';
// Types
import type { NavTabsLinksTabType } from '@/components/composite/NavTabs/NavTabLinks';
import NavTabsLinks from '@/components/composite/NavTabs/NavTabLinks';

type ManageProfilesHeaderProps = { children?: ReactNode };

export function ManageProfilesHeader({ children }: ManageProfilesHeaderProps) {
    const { t } = useTranslation('commons');
    const { hasGroupPermission } = useUserGroupRestriction();
    const { isOpen, toggle } = useToggle();

    const links: NavTabsLinksTabType[] = [
        {
            name: t('groups'),
            icon: 'group',
            iconActive: 'group-on',
            id: 'groups',
            url: hasGroupPermission ? GROUPS : '',
            isRestrictedByPlan: !hasGroupPermission,
            onClick: (ev: React.MouseEvent) => {
                if (!hasGroupPermission) {
                    ev.preventDefault();
                    toggle();
                }
            }
        },
        {
            name: t('social_profiles'),
            icon: 'single-profile',
            iconActive: 'single-profile-on',
            id: 'social_profiles',
            url: SOCIAL_PROFILES
        }
    ];
    return (
        <>
            <header className="mb-4 pb-4">
                <H2>{t('manage_profiles')}</H2>
            </header>
            <div className="mb-5 flex flex-col justify-between space-y-5 @xl/wrapper-platform-content:flex-row @xl/wrapper-platform-content:space-y-0">
                <NavTabsLinks tabs={links} />
                {children}
            </div>
            <ModalUpsell
                heading={t('upsell_modal.group.heading')}
                description={t('upsell_modal.group.description')}
                benefits={[
                    { text: t('upsell_modal.group.benefit1'), icon: 'check' },
                    { text: t('upsell_modal.group.benefit2'), icon: 'check' },
                    { text: t('upsell_modal.group.benefit3'), icon: 'check' },
                    { text: t('upsell_modal.group.benefit4'), icon: 'plus' }
                ]}
                illustrationPath="/images/modals/modalUpsell/group.png"
                dismiss={toggle}
                isOpen={isOpen}
            />
        </>
    );
}
